import React, { useEffect, useState } from "react";
import CollapseHeader from "../../../core/common/collapse-header/collapse-header";
import { useDispatch } from "react-redux";
import { AppDispatch } from "../../../core/data/redux/store";
import { fetchAllattendanceSummaryReport } from "../../../core/data/redux/reports/attendancesummary_report/attendancesummary.Action";
import dayjs from "dayjs";
import { message } from "antd";

interface AttendanceSummaryProps {
  fromDate?: string;
  toDate?: string;
  month?: string;
  year?: string;
  employee?: string;
   showOnlyTable?: boolean;
}


const monthNames = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December"
];

const AttendanceSummaryReport: React.FC<AttendanceSummaryProps> = ({
  fromDate: propFromDate,
  toDate: propToDate,
  month: propMonth,
  year: propYear,
  employee: propEmployee,
  showOnlyTable
}) => {
  console.log(propFromDate, propToDate, propEmployee)

  const dispatch = useDispatch<AppDispatch>();

  // ---------- DEFAULT DATES ----------
  const today = dayjs();
  const defaultFrom = today.startOf("month").format("YYYY-MM-DD");
  const defaultTo = today.format("YYYY-MM-DD");

  const [fromDate, setFromDate] = useState(defaultFrom);
  const [toDate, setToDate] = useState(defaultTo);

  const [attendanceData, setAttendanceData] = useState<Record<number, any[]>>({});
  const [employees, setEmployees] = useState<{ id: number; name: string }[]>([]);
  const [selectedEmployeeId, setSelectedEmployeeId] = useState<number | null>(null);
  const [filteredMonths, setFilteredMonths] = useState<any[]>([]);

  useEffect(() => {
    if (propFromDate) setFromDate(propFromDate);
    if (propToDate) setToDate(propToDate);
  }, [propFromDate, propToDate]);

  // ---------- APPLY PROPS ON FIRST LOAD ----------
  useEffect(() => {
    if (!propEmployee || employees.length === 0) return;

    const cleanedName = propEmployee.toLowerCase().trim();

    // Fuzzy match on both first+last and partial
    const found = employees.find((e) =>
      e.name.toLowerCase().includes(cleanedName)
    ) || employees.find((e) =>
      cleanedName.split(" ")
        .every(part => e.name.toLowerCase().includes(part))
    );

    if (found) {
      setSelectedEmployeeId(found.id);
    }
  }, [propEmployee, employees]);


  // ---------- DATE RANGE VALIDATION ----------
  const validateDateRange = () => {
    const start = dayjs(fromDate);
    const end = dayjs(toDate);

    if (end.isAfter(today)) {
      message.warning("To Date cannot be greater than today.");
      setToDate(defaultTo);
      return false;
    }

    if (end.diff(start, "month", true) > 4) {
      message.warning("Date range cannot exceed 4 months.");
      return false;
    }
    return true;
  };

  // ---------- API CALL ----------
  const fetchSummary = async () => {
    if (!validateDateRange()) return;

    const res: any = await dispatch(
      fetchAllattendanceSummaryReport({ fromDate, toDate })
    );

    const apiData = res.payload?.data || [];
    let transformedData: Record<number, any[]> = {};
    let empList: any[] = [];
    let monthSet: Set<string> = new Set();

    apiData.forEach((emp: any) => {
      empList.push({
        id: emp.EmployeeID,
        name: emp.Name,
      });

      let monthlyArr: any[] = [];

      try {
        monthlyArr =
          typeof emp.MonthlyAttendance === "string"
            ? JSON.parse(emp.MonthlyAttendance)
            : emp.MonthlyAttendance || [];
      } catch {
        monthlyArr = [];
      }

      const mapped = monthlyArr.map((item: any) => {
        const [year, month] = item.Month.split("-");
        const monthIndex = parseInt(month, 10) - 1;
        const monthName = monthNames[monthIndex];

        monthSet.add(monthName);

        return {
          month: monthName,
          present: item.PresentDays ?? 0,
          absent: item.AbsentCount ?? 0,
          leave: item.ApprovedLeaveCount ?? 0,
          rawMonth: item.Month,
        };
      });

      transformedData[emp.EmployeeID] = mapped;
    });

    setEmployees(empList);
    setAttendanceData(transformedData);

    const orderedMonths = Array.from(monthSet).map((m) => ({
      month: m,
      number: monthNames.indexOf(m) + 1,
    }));

    orderedMonths.sort((a, b) => a.number - b.number);

    setFilteredMonths(orderedMonths);
  };

  // ---------- ON LOAD API ----------
  useEffect(() => {
    fetchSummary();
  }, []);

  const handleSearch = () => {
    validateDateRange();
    fetchSummary();
  };

    if (showOnlyTable) {
  return (
    <div style={{ overflowX: "auto" }}>
                <table className="table table-bordered text-center">
                  <thead>
                    <tr>
                      <th
                        rowSpan={2}
                        style={{
                          backgroundColor: "#f8f9fa",
                          verticalAlign: "middle",
                          position: "sticky",
                          left: 0,
                          zIndex: 2
                        }}
                      >
                        Employee
                      </th>

                      {filteredMonths.map((m: any) => (
                        <th key={m.month} colSpan={3} style={{ backgroundColor: "#391694", color: "#fff" }}>
                          {m.month}
                        </th>
                      ))}
                    </tr>

                    <tr>
                      {filteredMonths.map((_, idx) => (
                        <React.Fragment key={idx}>
                          <th>Present</th>
                          <th>Absent</th>
                          <th>Leave</th>
                        </React.Fragment>
                      ))}
                    </tr>
                  </thead>

                  <tbody>
                    {employees
                      .filter((emp) => selectedEmployeeId === null || emp.id === selectedEmployeeId)
                      .map((emp) => {
                        const empData = attendanceData[emp.id] || [];
                        return (
                          <tr key={emp.id}>
                            <td style={{ position: "sticky", left: 0, background: "#fff" }}>
                              {emp.name}
                            </td>

                            {filteredMonths.map((m) => {
                              const row = empData.find((r) => r.month === m.month) || {
                                present: 0,
                                absent: 0,
                                leave: 0
                              };
                              return (
                                <React.Fragment key={m.month}>
                                  <td>{row.present}</td>
                                  <td>{row.absent}</td>
                                  <td>{row.leave}</td>
                                </React.Fragment>
                              );
                            })}
                          </tr>
                        );
                      })}
                  </tbody>
                </table>
              </div>
  );
}
  return (
    <>
      <div className="page-wrapper">
        <div className="content">

          {/* Breadcrumb */}
          <div className="d-md-flex d-block align-items-center justify-content-between page-breadcrumb mb-3">
            <div className="my-auto mb-2">
              <h2 className="mb-1">Attendance Summary Report</h2>
            </div>
            <div className="head-icons ms-2">
              <CollapseHeader />
            </div>
          </div>

          {/* Date Filters */}
          <div className="row mb-4 align-items-end">

            <div className="col-md-3">
              <label className="form-label fw-semibold">From Date</label>
              <input
                type="date"
                className="form-control"
                max={defaultTo}
                value={fromDate}
                onChange={(e) => setFromDate(e.target.value)}
              />
            </div>

            <div className="col-md-3">
              <label className="form-label fw-semibold">To Date</label>
              <input
                type="date"
                className="form-control"
                max={defaultTo}
                value={toDate}
                onChange={(e) => setToDate(e.target.value)}
              />
            </div>

            <div className="col-md-2">
              <button
                className="btn btn-primary w-100"
                style={{ marginTop: "32px" }}
                onClick={handleSearch}
              >
                🔍 Search
              </button>
            </div>

          </div>

          {/* Attendance Table */}
          <div className="card">
            <div className="card-body">

              <h6 className="fw-bold mb-3">Attendance Overview</h6>

              {/* Employee Filter */}
              <div className="mb-3">
                <label className="form-label fw-semibold me-2">Filter by Employee:</label>
                <select
                  className="form-select w-auto d-inline-block"
                  value={selectedEmployeeId ?? ""}
                  onChange={(e) =>
                    setSelectedEmployeeId(e.target.value === "" ? null : parseInt(e.target.value))
                  }
                >
                  <option value="">All Employees</option>
                  {employees.map((emp) => (
                    <option key={emp.id} value={emp.id}>
                      {emp.name}
                    </option>
                  ))}
                </select>
              </div>

              {/* Summary Table */}
              <div style={{ overflowX: "auto" }}>
                <table className="table table-bordered text-center">
                  <thead>
                    <tr>
                      <th
                        rowSpan={2}
                        style={{
                          backgroundColor: "#f8f9fa",
                          verticalAlign: "middle",
                          position: "sticky",
                          left: 0,
                          zIndex: 2
                        }}
                      >
                        Employee
                      </th>

                      {filteredMonths.map((m: any) => (
                        <th key={m.month} colSpan={3} style={{ backgroundColor: "#391694", color: "#fff" }}>
                          {m.month}
                        </th>
                      ))}
                    </tr>

                    <tr>
                      {filteredMonths.map((_, idx) => (
                        <React.Fragment key={idx}>
                          <th>Present</th>
                          <th>Absent</th>
                          <th>Leave</th>
                        </React.Fragment>
                      ))}
                    </tr>
                  </thead>

                  <tbody>
                    {employees
                      .filter((emp) => selectedEmployeeId === null || emp.id === selectedEmployeeId)
                      .map((emp) => {
                        const empData = attendanceData[emp.id] || [];
                        return (
                          <tr key={emp.id}>
                            <td style={{ position: "sticky", left: 0, background: "#fff" }}>
                              {emp.name}
                            </td>

                            {filteredMonths.map((m) => {
                              const row = empData.find((r) => r.month === m.month) || {
                                present: 0,
                                absent: 0,
                                leave: 0
                              };
                              return (
                                <React.Fragment key={m.month}>
                                  <td>{row.present}</td>
                                  <td>{row.absent}</td>
                                  <td>{row.leave}</td>
                                </React.Fragment>
                              );
                            })}
                          </tr>
                        );
                      })}
                  </tbody>
                </table>
              </div>

            </div>
          </div>

        </div>

        <div className="footer border-top bg-white p-3">
          <p className="mb-0">2024 - 2025© hrmtree.</p>
        </div>
      </div>
    </>
  );
};

export default AttendanceSummaryReport;
